import { useLingui } from '@lingui/react/macro'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import {
  MinimapContainer,
  MinimapImage,
  MinimapImageWrapper,
  MinimapMultipageContainer,
  ViewportIndicator,
} from './documentMinimapStyles'
import {
  clampPanPosition,
  getPanBounds,
  MINIMAP_ALIGNMENT_PADDING_PX,
  panFromMinimapDrag,
} from './minimapPanBounds'
import {
  bindMinimapOverlayWheel,
  type MinimapWheelMode,
} from './retargetMinimapWheel'
import { TransformState } from './types'

const MINIMAP_MAX_WIDTH = 192
const MINIMAP_MAX_HEIGHT = 144

interface DocumentMinimapProps {
  imageUrls: string[]
  transformState: TransformState
  imageNaturalDimensions: { w: number; h: number }
  wrapperDimensions: { w: number; h: number }
  onNavigate: (positionX: number, positionY: number, animated: boolean) => void
  isMultipage: boolean
  wheelTargetRef: { readonly current: HTMLElement | null }
  wheelMode: MinimapWheelMode
}

export const DocumentMinimap: React.FC<DocumentMinimapProps> = ({
  imageUrls,
  transformState,
  imageNaturalDimensions,
  wrapperDimensions,
  onNavigate,
  isMultipage,
  wheelTargetRef,
  wheelMode,
}) => {
  const { t } = useLingui()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const wheelListenerCleanupRef = useRef<(() => void) | null>(null)
  const wheelModeRef = useRef(wheelMode)
  wheelModeRef.current = wheelMode
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef<{
    startClientX: number
    startClientY: number
    startPosX: number
    startPosY: number
  } | null>(null)
  const activePointerIdRef = useRef<number | null>(null)
  const lastPosRef = useRef({
    x: transformState.positionX,
    y: transformState.positionY,
  })

  const { scale, positionX, positionY } = transformState
  const { w: imgW, h: imgH } = imageNaturalDimensions
  const { w: wrapperW, h: wrapperH } = wrapperDimensions

  const overlayRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node
      wheelListenerCleanupRef.current?.()
      wheelListenerCleanupRef.current = null
      if (!node) return
      wheelListenerCleanupRef.current = bindMinimapOverlayWheel(
        node,
        () => wheelTargetRef.current,
        () => wheelModeRef.current
      )
    },
    [wheelTargetRef]
  )

  const minimapScale = useMemo(() => {
    if (imgW === 0 || imgH === 0) return 1
    return Math.min(MINIMAP_MAX_WIDTH / imgW, MINIMAP_MAX_HEIGHT / imgH)
  }, [imgW, imgH])

  const minimapWidth = imgW * minimapScale
  const minimapHeight = imgH * minimapScale

  const panBounds = useMemo(
    () => getPanBounds(wrapperW, wrapperH, imgW * scale, imgH * scale),
    [wrapperW, wrapperH, imgW, imgH, scale]
  )

  const viewportRect = useMemo(() => {
    if (scale === 0) return { x: 0, y: 0, w: minimapWidth, h: minimapHeight }
    return {
      x: (-positionX / scale) * minimapScale,
      y: (-positionY / scale) * minimapScale,
      w: (wrapperW / scale) * minimapScale,
      h: (wrapperH / scale) * minimapScale,
    }
  }, [
    scale,
    positionX,
    positionY,
    wrapperW,
    wrapperH,
    minimapScale,
    minimapWidth,
    minimapHeight,
  ])

  const clampedViewport = useMemo(() => {
    const x = Math.max(0, Math.min(viewportRect.x, minimapWidth))
    const y = Math.max(0, Math.min(viewportRect.y, minimapHeight))
    const w = Math.min(viewportRect.w, minimapWidth - x)
    const h = Math.min(viewportRect.h, minimapHeight - y)
    return { x, y, w, h }
  }, [viewportRect, minimapWidth, minimapHeight])

  const navigateClamped = useCallback(
    (nextX: number, nextY: number, animated: boolean, paddingPx: number) => {
      const clamped = clampPanPosition(nextX, nextY, panBounds, paddingPx)
      lastPosRef.current = { x: clamped.positionX, y: clamped.positionY }
      onNavigate(clamped.positionX, clamped.positionY, animated)
    },
    [onNavigate, panBounds]
  )

  const navigateToMinimapPoint = useCallback(
    (minimapX: number, minimapY: number, animated: boolean) => {
      const imgX = minimapX / minimapScale
      const imgY = minimapY / minimapScale
      const newPosX = wrapperW / 2 - imgX * scale
      const newPosY = wrapperH / 2 - imgY * scale
      navigateClamped(newPosX, newPosY, animated, 0)
    },
    [minimapScale, wrapperW, wrapperH, scale, navigateClamped]
  )

  const handleMinimapClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      const minimapX = e.clientX - rect.left
      const minimapY = e.clientY - rect.top
      navigateToMinimapPoint(minimapX, minimapY, true)
    },
    [isDragging, navigateToMinimapPoint]
  )

  const handleViewportPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      activePointerIdRef.current = e.pointerId
      setIsDragging(true)
      lastPosRef.current = { x: positionX, y: positionY }
      dragStartRef.current = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPosX: positionX,
        startPosY: positionY,
      }
    },
    [positionX, positionY]
  )

  const handleViewportPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerIdRef.current !== e.pointerId) return
      if (!dragStartRef.current) return
      const next = panFromMinimapDrag({
        startPosX: dragStartRef.current.startPosX,
        startPosY: dragStartRef.current.startPosY,
        startClientX: dragStartRef.current.startClientX,
        startClientY: dragStartRef.current.startClientY,
        clientX: e.clientX,
        clientY: e.clientY,
        minimapScale,
        documentScale: scale,
      })
      navigateClamped(
        next.positionX,
        next.positionY,
        false,
        MINIMAP_ALIGNMENT_PADDING_PX
      )
    },
    [minimapScale, scale, navigateClamped]
  )

  const handleViewportPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerIdRef.current !== e.pointerId) return
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      activePointerIdRef.current = null
      dragStartRef.current = null
      setIsDragging(false)
      const { x, y } = lastPosRef.current
      navigateClamped(x, y, true, 0)
    },
    [navigateClamped]
  )

  const stopIndicatorClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
    },
    []
  )

  if (imgW === 0 || imgH === 0) return null

  return (
    <MinimapContainer ref={overlayRef} onClick={handleMinimapClick}>
      <MinimapImageWrapper $width={minimapWidth} $height={minimapHeight}>
        {isMultipage ? (
          <MinimapMultipageContainer>
            {imageUrls.map((url, index) => {
              const pageNumber = index + 1
              return (
                <MinimapImage
                  key={index}
                  src={url}
                  alt={t`minimap page ${pageNumber}`}
                  draggable={false}
                />
              )
            })}
          </MinimapMultipageContainer>
        ) : (
          <MinimapImage src={imageUrls[0]} alt={t`minimap`} draggable={false} />
        )}
        <ViewportIndicator
          $left={clampedViewport.x}
          $top={clampedViewport.y}
          $width={clampedViewport.w}
          $height={clampedViewport.h}
          onPointerDown={handleViewportPointerDown}
          onPointerMove={handleViewportPointerMove}
          onPointerUp={handleViewportPointerUp}
          onPointerCancel={handleViewportPointerUp}
          onClick={stopIndicatorClick}
        />
      </MinimapImageWrapper>
    </MinimapContainer>
  )
}
